import { useRef } from 'react';
import { DataTable as PrimeDataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';

import {
  Button,
  DataTable,
  PageActions,
  Paginator,
  SkeletonLoader,
} from 'components';
import { humanReadable } from 'utils';
import { locales } from 'constants/index';

import { ListZeroUiProps, FlatSubmission } from './ListZero.types';

import './list-zero.scss';

function getDuration(startTime: string, endTime: string) {
  if (!startTime || !endTime) return 'N/A';
  const ms = new Date(endTime).getTime() - new Date(startTime).getTime();
  return `${(ms / 1000).toFixed(1)}s`;
}

function StatusBodyTemplate(row: FlatSubmission) {
  return row.success ? locales.general.success : locales.general.unsuccessful;
}

function CreatedDateBodyTemplate(row: FlatSubmission) {
  return humanReadable(row.createdDate);
}

function DurationBodyTemplate(row: FlatSubmission) {
  return getDuration(row.startTime, row.endTime);
}

const submissionColumns = [
  { header: 'Study ID', field: 'studyId', sortable: true },
  { header: 'Status', field: 'success', sortable: true, body: StatusBodyTemplate },
  { header: 'Status Code', field: 'statusCode', sortable: true },
  { header: 'Employees', field: 'dataReceived', sortable: true },
  { header: 'Duration', field: 'duration', sortable: false, body: DurationBodyTemplate },
  { header: 'Message', field: 'message', sortable: true },
  { header: 'Deleted Employees', field: 'deletedEmployees', sortable: false },
  { header: 'Created Date', field: 'createdDate', sortable: true, body: CreatedDateBodyTemplate },
];

export default function ListZeroUi({
  isLoading,
  employees,
  fileName,
  response,
  error,
  recentSubmissions,
  totalSubmissions,
  isLoadingSubmissions,
  first,
  rows,
  handlers,
}: ListZeroUiProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="list-zero">
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handlers.handleFileUpload}
        style={{ display: 'none' }}
      />

      <PageActions>
        <Button
          onClick={handlers.handleDownloadSample}
          label="Download Sample CSV"
          outlined
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          label={fileName ? 'Change File' : 'Upload CSV'}
          outlined
        />
        {employees.length > 0 && (
          <>
            <Button onClick={handlers.handleClear} label="Clear" outlined />
            <Button
              onClick={handlers.handleSubmit}
              label={`Submit ${employees.length} employees`}
              disabled={isLoading}
            />
          </>
        )}
      </PageActions>

      {fileName && (
        <div className="list-zero__file-info">
          <i className="pi pi-file" />
          <span>{fileName}</span>
          <span className="list-zero__badge">{employees.length} employees</span>
        </div>
      )}

      {isLoading && (
        <div className="list-zero__loading">
          <ProgressSpinner style={{ width: '40px', height: '40px' }} />
          <span>Submitting...</span>
        </div>
      )}

      {response && (
        <div className={`list-zero__response list-zero__response--${response.success ? 'success' : 'error'}`}>
          <h4>{response.success ? 'Response' : 'Error'}</h4>
          <p><strong>Message:</strong> {response.message}</p>
          <p><strong>Employees Received:</strong> {response.dataReceived}</p>
          <p><strong>Study ID:</strong> {response.studyId}</p>
          {response.deletedEmployees && response.deletedEmployees.length > 0 && (
            <div className="list-zero__deleted-employees">
              <p><strong>Missing Employees (AZADEA IDs):</strong></p>
              <ul>
                {response.deletedEmployees.map((id) => (
                  <li key={id}>{id}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="list-zero__response list-zero__response--error">
          <h4>Error</h4>
          <pre>{error}</pre>
        </div>
      )}

      {employees.length > 0 && (
        <div className="list-zero__table">
          <h3>Preview ({employees.length} employees)</h3>
          <PrimeDataTable
            value={employees}
            scrollable
            scrollHeight="400px"
            size="small"
            stripedRows
            paginator
            rows={20}
            rowsPerPageOptions={[10, 20, 50]}
          >
            <Column field="AZADEAID" header="AZADEA ID" frozen style={{ minWidth: '100px' }} />
            <Column field="EMPFIRSTNAME" header="First Name" style={{ minWidth: '120px' }} />
            <Column field="EMPFAMILYNAME" header="Family Name" style={{ minWidth: '120px' }} />
            <Column field="COUNTRY" header="Country" style={{ minWidth: '100px' }} />
            <Column field="COMPANYCODE" header="Company" style={{ minWidth: '100px' }} />
            <Column field="COMPANYNAME" header="Company Name" style={{ minWidth: '200px' }} />
            <Column field="POSITION" header="Position" style={{ minWidth: '200px' }} />
            <Column field="CLASS" header="Class" style={{ minWidth: '180px' }} />
            <Column field="LEAVEDATE" header="Leave Date" style={{ minWidth: '100px' }} />
            <Column field="LEAVEREASON" header="Leave Reason" style={{ minWidth: '120px' }} />
            <Column field="NATIONALID" header="National ID" style={{ minWidth: '120px' }} />
            <Column field="EMIRATESID" header="Emirates ID" style={{ minWidth: '150px' }} />
            <Column field="TIMESET" header="Timeset" style={{ minWidth: '80px' }} />
            <Column field="USER_PERSON_TYPE" header="Person Type" style={{ minWidth: '140px' }} />
          </PrimeDataTable>
        </div>
      )}

      <div className="list-zero__recent">
        {isLoadingSubmissions ? (
          <SkeletonLoader height="8rem" />
        ) : (
          <DataTable
            sortMode="multiple"
            totalRecords={totalSubmissions}
            columns={submissionColumns}
            value={recentSubmissions}
          />
        )}
        {recentSubmissions.length > 0 && !isLoadingSubmissions && (
          <Paginator
            first={first}
            rows={rows}
            totalRecords={totalSubmissions}
            rowsPerPageOptions={[20, 50]}
            onPageChange={handlers.handlePageChange}
          />
        )}
      </div>
    </div>
  );
}
